import { ReactNode } from "react";
import styled from "styled-components";
import {
  Content as ContentBase,
  Item as ItemBase,
  Trigger as TriggerBase,
} from "@radix-ui/react-accordion";
import { shade0, shade800 } from "../../../styles/tokens";
import { StatusChipVariant } from "../../status-chip/StatusChip.Parts";

// TEXT

// STYLES
export const Item = styled(ItemBase)<{ $showBorder?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;

  border-top: ${({ $showBorder = true }) =>
    $showBorder ? `1px solid ${shade800}` : "none"};

  &:last-child {
    border-bottom: ${({ $showBorder = true }) =>
      $showBorder ? `1px solid ${shade800}` : "none"};
  }
`;

export const Trigger = styled(TriggerBase)<{
  disabled?: boolean;
  $padding?: string;
}>`
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 1rem;
  overflow-wrap: break-word;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  background: transparent;
  padding: ${({ $padding }) => $padding ?? "1.5rem 0.5rem 1.5rem 0.5rem"};
  text-align: start;
  &:focus-visible {
    box-shadow: 0 0 0 2px ${shade0};
  }
`;

export const ToggleIconContainer = styled.div`
  margin: 0;
  display: flex;
  flex-shrink: 0;

  /* Show PlusIcon when closed, hide MinusIcon */
  svg:first-child {
    display: block;
  }

  svg:last-child {
    display: none;
  }

  /* Hide PlusIcon when open, show MinusIcon - Trigger (button) has data-state */
  button[data-state="open"] & svg:first-child {
    display: none;
  }

  button[data-state="open"] & svg:last-child {
    display: block;
  }
`;

export const Content = styled(ContentBase)<{ $padding?: string }>`
  width: 100%;
  padding: ${({ $padding }) => $padding ?? "0 0 1rem 0.5rem"};
  overflow: hidden;

  &[data-state="open"] {
    animation: slideDown 0.3s ease-out;
  }

  &[data-state="closed"] {
    animation: slideUp 0.3s ease-in;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      height: 0;
    }
    to {
      opacity: 1;
      height: var(--radix-accordion-content-height);
    }
  }

  @keyframes slideUp {
    from {
      opacity: 1;
      height: var(--radix-accordion-content-height);
    }
    to {
      opacity: 0;
      height: 0;
    }
  }
`;

export const ItemNumberContainer = styled.div`
  width: 4rem;
`;

export const TitlesContainer = styled.div`
  flex: 1;
`;

// DATA

// TYPES
export interface Props {
  /** Unique value of this item (for controlled open/close control) */
  value?: string;
  /** The item's header label */
  title?: string | ReactNode;
  /** The item's header subtitle */
  subtitle?: string;
  /** The item's header status */
  status?: string;
  /** The item's header status variant */
  statusVariant?: StatusChipVariant;
  /** Whether to show numbering in the header */
  numbered?: boolean;
  /** What style of toggle icon to use */
  toggleIcon?: "plus-minus" | "chevron";
  /** The item's hidden content panel */
  content: ReactNode;
  /** Whether the overall accordion is disabled */
  disabled?: boolean;
  /** The index of this item in the accordion (0-based) */
  index?: number;
  /** Whether the item has borders
   * @default true
   */
  showBorder?: boolean;
  /** The padding of the trigger
   * @default "0 0 1rem 0.5rem"
   */
  $padding?: string;
}

export type SkeletonItemProps = Pick<
  Props,
  "numbered" | "status" | "subtitle" | "title" | "index" | "showBorder"
>;

// CONSTANTS

// FUNCTIONS
export const padNumber = (number: number) =>
  String(number + 1).padStart(2, "0");

// ASSETS
